import { useClubFilters } from '@/context/ClubFiltersContext';
import {
  GAME_TYPE_FILTER_OPTIONS,
  PLATFORM_FILTER_OPTIONS,
  STAKES_FILTER_OPTIONS,
} from '@/services/clubFilterService';
import styles from '@/components/Layout/SidebarFilters.module.css';

interface FilterCheckboxGroupProps<T extends string> {
  legend: string;
  options: { key: T; label: string }[];
  selected: T[];
  onToggle: (key: T) => void;
}

function FilterCheckboxGroup<T extends string>({
  legend,
  options,
  selected,
  onToggle,
}: FilterCheckboxGroupProps<T>) {
  return (
    <fieldset className={styles.filterGroup}>
      <legend className={styles.filterLegend}>{legend}</legend>
      <ul className={styles.filterList}>
        {options.map((option) => {
          const inputId = `${legend}-${option.key}`.toLowerCase().replace(/\s+/g, '-');
          const isChecked = selected.includes(option.key);

          return (
            <li key={option.key}>
              <label className={isChecked ? `${styles.filterOption} ${styles.filterOptionActive}` : styles.filterOption}>
                <input
                  id={inputId}
                  type="checkbox"
                  className={styles.filterCheckbox}
                  checked={isChecked}
                  onChange={() => onToggle(option.key)}
                />
                <span className={styles.filterLabel}>{option.label}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}

export function SidebarFilters() {
  const { filters, toggleGameType, togglePlatform, toggleStakes, clearFilters, hasActiveFilters } =
    useClubFilters();

  return (
    <section className={styles.filters} aria-label="Club filters">
      <div className={styles.filtersHeader}>
        <h2 className={styles.filtersTitle}>Filters</h2>
        {hasActiveFilters && (
          <button type="button" className={styles.clearButton} onClick={clearFilters}>
            Clear
          </button>
        )}
      </div>

      <FilterCheckboxGroup
        legend="Game type"
        options={GAME_TYPE_FILTER_OPTIONS}
        selected={filters.gameTypes}
        onToggle={toggleGameType}
      />

      <FilterCheckboxGroup
        legend="Platform"
        options={PLATFORM_FILTER_OPTIONS}
        selected={filters.platforms}
        onToggle={togglePlatform}
      />

      <FilterCheckboxGroup
        legend="Stakes"
        options={STAKES_FILTER_OPTIONS}
        selected={filters.stakes}
        onToggle={toggleStakes}
      />
    </section>
  );
}
